import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import baseUrl from "../../api/baseUrl";

function canSendOrderToPartner(partner: Partner) {
  return (
    !!partner.labId &&
    !partner.isShadow &&
    (partner.canSendOrders ?? partner.status === "active")
  );
}

function canTrackLocalPartner(partner: Partner) {
  return (
    !!partner.isShadow &&
    (partner.canTrackOrders ?? partner.status === "active")
  );
}

type Partner = {
  id: string;
  labId: string | null;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  isShadow?: boolean;
  status?: string;
  canSendOrders?: boolean;
  canTrackOrders?: boolean;
};

type DirectoryLab = {
  id: string;
  labId: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
};

type LocalLabForm = {
  labName: string;
  contactName: string;
  phone: string;
  email: string;
  address: string;
};

const emptyLocalLabForm = (): LocalLabForm => ({
  labName: "",
  contactName: "",
  phone: "",
  email: "",
  address: "",
});

function LabPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [directory, setDirectory] = useState<DirectoryLab[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [addingLabId, setAddingLabId] = useState<string | null>(null);
  const [addingLocalLab, setAddingLocalLab] = useState(false);
  const [showLocalForm, setShowLocalForm] = useState(false);
  const [localForm, setLocalForm] = useState<LocalLabForm>(emptyLocalLabForm);
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    baseUrl
      .get("client/labs/partners")
      .then((res) => setPartners(res.data.data ?? []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  const loadDirectory = useCallback(() => {
    setDirectoryLoading(true);
    baseUrl
      .get("client/labs/partners/search")
      .then((res) => {
        const list = (res.data.directory ?? []).map(
          (l: Record<string, string>) => ({
            id: l.id,
            labId: l.id,
            name: l.name,
            email: l.email,
            phone: l.contactNumber,
            address: l.address,
          })
        );
        setDirectory(list);
      })
      .catch(() => setDirectory([]))
      .finally(() => setDirectoryLoading(false));
  }, []);

  useEffect(() => {
    load();
    loadDirectory();
  }, [load, loadDirectory]);

  function addLab(labId: string) {
    if (!labId) return;
    setMessage("");
    setAddingLabId(labId);
    baseUrl
      .post("client/labs/partners", { labId })
      .then((res) => {
        setMessage(res.data?.message || "Lab added to your partners");
        load();
        loadDirectory();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to add lab")
      )
      .finally(() => setAddingLabId(null));
  }

  function addLocalLab(e: React.FormEvent) {
    e.preventDefault();
    const labName = localForm.labName.trim();
    if (!labName) {
      setMessage("Lab name is required");
      return;
    }
    setMessage("");
    setAddingLocalLab(true);
    baseUrl
      .post("client/labs/partners", {
        clinicLocalProfile: {
          labName,
          contactName: localForm.contactName.trim() || undefined,
          phone: localForm.phone.trim() || undefined,
          email: localForm.email.trim() || undefined,
          address: localForm.address.trim() || undefined,
        },
      })
      .then((res) => {
        setMessage(res.data?.message || "Personal lab added");
        setLocalForm(emptyLocalLabForm());
        setShowLocalForm(false);
        load();
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || "Failed to add personal lab")
      )
      .finally(() => setAddingLocalLab(false));
  }

  return (
    <div className="w-full p-4 md:p-6 lg:p-8">
      <h1 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
        Partner labs
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        Add registered labs from the directory, or add your own lab if it is not
        on the platform yet. Personal labs are only visible to your clinic.
      </p>

      {message && (
        <p
          className={`mb-4 text-sm px-3 py-2 rounded-lg ${
            message.toLowerCase().includes("fail") ||
            message.toLowerCase().includes("required")
              ? "text-red-700 bg-red-50 dark:bg-red-950/40 dark:text-red-300"
              : "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300"
          }`}
        >
          {message}
        </p>
      )}

      <div className="mb-6 rounded-xl border dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
          <div>
            <h2 className="text-sm font-semibold">Add your own lab</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              For labs not registered on the platform. Only your clinic can see
              this entry.
            </p>
          </div>
          {!showLocalForm && (
            <button
              type="button"
              onClick={() => {
                setMessage("");
                setShowLocalForm(true);
              }}
              className="shrink-0 px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary"
            >
              Add personal lab
            </button>
          )}
        </div>
        {showLocalForm && (
          <form onSubmit={addLocalLab} className="p-4 space-y-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Lab name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={localForm.labName}
                onChange={(e) =>
                  setLocalForm((prev) => ({ ...prev, labName: e.target.value }))
                }
                placeholder="Enter lab name"
                className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Contact name
                </label>
                <input
                  type="text"
                  value={localForm.contactName}
                  onChange={(e) =>
                    setLocalForm((prev) => ({
                      ...prev,
                      contactName: e.target.value,
                    }))
                  }
                  placeholder="Contact person"
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={localForm.phone}
                  onChange={(e) =>
                    setLocalForm((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  placeholder="Phone number"
                  className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Email
              </label>
              <input
                type="email"
                value={localForm.email}
                onChange={(e) =>
                  setLocalForm((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="Email address"
                className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Address
              </label>
              <textarea
                value={localForm.address}
                onChange={(e) =>
                  setLocalForm((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder="Lab address"
                rows={2}
                className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="submit"
                disabled={addingLocalLab}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary disabled:opacity-50"
              >
                {addingLocalLab ? "Saving…" : "Save personal lab"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLocalForm(false);
                  setLocalForm(emptyLocalLabForm());
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg border dark:border-slate-600 text-slate-600 dark:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {(directoryLoading || directory.length > 0) && (
        <div className="mb-6 rounded-xl border dark:border-slate-700 overflow-hidden">
          <h2 className="px-4 py-3 text-sm font-semibold bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
            Registered labs you can add
          </h2>
          {directoryLoading ? (
            <p className="p-6 text-sm text-slate-500">Loading labs…</p>
          ) : (
            <ul className="divide-y dark:divide-slate-700">
              {directory.map((lab) => (
                <li
                  key={lab.id}
                  className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 dark:text-white">
                      {lab.name}
                    </p>
                    {lab.address?.trim() && (
                      <p className="text-slate-500 text-xs mt-0.5">
                        {lab.address.trim()}
                      </p>
                    )}
                    <p className="text-slate-500 text-xs mt-0.5">
                      {[lab.email, lab.phone]
                        .map((v) => v?.trim())
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => addLab(lab.labId)}
                    disabled={addingLabId === lab.labId}
                    className="shrink-0 px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary disabled:opacity-50"
                  >
                    {addingLabId === lab.labId ? "Adding…" : "Add"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-xl border dark:border-slate-700 overflow-x-auto">
        <h2 className="px-4 py-3 text-sm font-semibold bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
          Your partner labs
        </h2>
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : partners.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            No partner labs yet. Add one from the directory or create a personal
            lab above.
          </p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80">
                <th className="px-4 py-3 font-medium text-slate-500">Lab</th>
                <th className="px-4 py-3 font-medium text-slate-500">Type</th>
                <th className="px-4 py-3 font-medium text-slate-500">Address</th>
                <th className="px-4 py-3 font-medium text-slate-500">Contact</th>
                <th className="px-4 py-3 font-medium text-slate-500 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-slate-100 dark:border-slate-700/80 hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                >
                  <td className="px-4 py-3 font-medium text-slate-800 dark:text-white">
                    {p.name?.trim() || "Lab"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        p.isShadow
                          ? "inline-flex rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300"
                          : "inline-flex rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300"
                      }
                    >
                      {p.isShadow ? "Added locally" : "Registered lab"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {p.address?.trim() || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {[p.email, p.phone]
                      .map((v) => v?.trim())
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canSendOrderToPartner(p) ? (
                      <Link
                        to={`/client/labs/send-order?labId=${String(p.labId)}`}
                        className="inline-flex rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                      >
                        Send order
                      </Link>
                    ) : canTrackLocalPartner(p) ? (
                      <Link
                        to={`/client/labs/send-order?partnerId=${p.id}`}
                        className="inline-flex rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                      >
                        Track order
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default LabPartners;
